/**
 * The one card every game detail section sits in (Scores UX spec v1.0
 * section 7.3), so the Matchup, Box score and Plays tabs read as one family:
 * `surface`, a 1px `border-hairline`, radius 12, 12 padding, then a header of
 * an optional 20px mark, the title in `bodyStrong` 16, an optional `small`
 * caption and an optional trailing slot, 12 above the content.
 *
 * A section never renders loose text outside its card. Inside one, an empty
 * section reads "No data yet" in `body` `textSecondary`
 * (`GAME_SECTION_EMPTY_LINE`).
 *
 * `bleed` drops the side padding for a table that runs edge to edge inside
 * the card (the line score, the box score); its header keeps the 12 inset.
 */
import { cn } from "@sportsmet/ui";
import type { ReactNode } from "react";

export const GAME_SECTION_EMPTY_LINE = "No data yet";

export type typeGameSectionCardProps = {
  testId?: string | undefined;
  title?: string | undefined;
  /** A 20px mark before the title. */
  mark?: ReactNode;
  caption?: string | undefined;
  trailing?: ReactNode;
  /** Table content that runs to the card's side edges. */
  bleed?: boolean | undefined;
  className?: string | undefined;
  dataAttributes?: Record<`data-${string}`, string | undefined>;
  children: ReactNode;
};

/** The card frame alone, for a section that draws its own header row. */
export const GAME_SECTION_CARD_CLASS =
  "flex flex-col gap-3 overflow-hidden rounded-md border border-hairline bg-surface";

export const GameSectionCard = (props: typeGameSectionCardProps): ReactNode => {
  const hasHeader =
    props.title !== undefined ||
    props.mark !== undefined ||
    props.trailing !== undefined;
  return (
    <section
      data-testid={props.testId}
      data-game-section-card=""
      {...props.dataAttributes}
      className={cn(
        GAME_SECTION_CARD_CLASS,
        props.bleed === true ? "py-3" : "p-3",
        props.className,
      )}
    >
      {hasHeader ? (
        <div
          data-testid="game-section-header"
          className={cn(
            "flex min-w-0 items-center gap-2",
            props.bleed === true ? "px-3" : "",
          )}
        >
          {props.mark}
          <div className="flex min-w-0 flex-1 flex-col">
            {props.title !== undefined ? (
              <h3 className="text-body-strong text-ink">{props.title}</h3>
            ) : null}
            {props.caption !== undefined ? (
              <p className="text-small text-ink-secondary">{props.caption}</p>
            ) : null}
          </div>
          {props.trailing !== undefined ? (
            <div className="flex shrink-0 items-center">{props.trailing}</div>
          ) : null}
        </div>
      ) : null}
      {props.children}
    </section>
  );
};

/** The empty line inside a card. */
export const GameSectionEmpty = (props: {
  testId?: string | undefined;
  line?: string | undefined;
}): ReactNode => (
  <p data-testid={props.testId} className="text-body text-ink-secondary">
    {props.line ?? GAME_SECTION_EMPTY_LINE}
  </p>
);
